import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  Mail, Send, ShieldCheck, RefreshCw, AlertCircle, CheckCircle2,
  Clock, Users, TrendingUp, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const SECRET_STORAGE_KEY = "nexus_admin_digest_secret";

/**
 * /admin/digest — operational dashboard for the weekly digest cron.
 * Secret-gated: the DIGEST_ADMIN_SECRET is entered once and persisted to
 * sessionStorage (NOT localStorage — disappears when the tab closes).
 * From here you can: see the last 12 runs, run a dry-run scan, fan-out the
 * digest manually, and send a test digest to any email.
 */
export default function AdminDigestPage() {
  const [secret, setSecret] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [expandedRun, setExpandedRun] = useState(null);

  useEffect(() => {
    const stored = sessionStorage.getItem(SECRET_STORAGE_KEY);
    if (stored) {
      setSecret(stored);
      setUnlocked(true);
    }
  }, []);

  const tryUnlock = async () => {
    if (!secret.trim()) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/digest/runs?secret=${encodeURIComponent(secret.trim())}&limit=12`);
      if (r.status === 401) {
        toast.error("Wrong secret");
        return;
      }
      if (!r.ok) throw new Error(String(r.status));
      const data = await r.json();
      setRuns(data.runs || []);
      setUnlocked(true);
      sessionStorage.setItem(SECRET_STORAGE_KEY, secret.trim());
    } catch (e) {
      toast.error("Couldn't fetch runs");
      console.warn(e);
    } finally {
      setLoading(false);
    }
  };

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/digest/runs?secret=${encodeURIComponent(secret)}&limit=12`);
      if (!r.ok) throw new Error(String(r.status));
      const data = await r.json();
      setRuns(data.runs || []);
    } catch (e) {
      console.warn(e);
    } finally {
      setLoading(false);
    }
  };

  const dryRun = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/digest/run-weekly?secret=${encodeURIComponent(secret)}&dry_run=true`, {
        method: "POST",
      });
      const data = await r.json();
      toast.success(`Dry run · ${data.active_creators} active creators · ${data.results?.skipped_no_email || 0} no email`);
    } catch {
      toast.error("Dry run failed");
    } finally {
      setBusy(false);
    }
  };

  const realRun = async () => {
    if (!window.confirm("Send the weekly digest to ALL active creators NOW? This is not reversible.")) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/admin/digest/run-weekly?secret=${encodeURIComponent(secret)}`, {
        method: "POST",
      });
      const data = await r.json();
      toast.success(`Run complete · ${data.results?.sent || 0} sent`);
      await refresh();
    } catch {
      toast.error("Run failed");
    } finally {
      setBusy(false);
    }
  };

  const testSend = async () => {
    if (!testEmail.trim()) return;
    setBusy(true);
    try {
      const r = await fetch(
        `${API}/admin/digest/test-send?secret=${encodeURIComponent(secret)}&email=${encodeURIComponent(testEmail.trim())}`,
        { method: "POST" }
      );
      if (r.status === 404) {
        toast.error("No NEXUS user with that email");
        return;
      }
      const data = await r.json();
      if (data.sent) toast.success(`Sent to ${data.to}`);
      else if (data.mock) toast.message("Resend not configured", { description: "Email would go through once DNS is verified." });
      else toast.error("Send failed");
    } catch {
      toast.error("Test send failed");
    } finally {
      setBusy(false);
    }
  };

  const totals = useMemo(() => {
    if (!runs.length) return null;
    return {
      runs: runs.length,
      sent: runs.reduce((s, r) => s + (r.results?.sent || 0), 0),
      errors: runs.reduce((s, r) => s + (r.results?.errors || 0), 0),
      avg_active: Math.round(runs.reduce((s, r) => s + (r.active_creators || 0), 0) / runs.length),
    };
  }, [runs]);

  if (!unlocked) {
    return (
      <div className="max-w-md mx-auto mt-20">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-heavy rounded-3xl p-8"
          data-testid="digest-admin-gate"
        >
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#DD2A7B] font-semibold mb-2 flex items-center gap-1.5">
            <ShieldCheck size={11} /> Admin · gated
          </div>
          <h1 className="font-display text-3xl font-bold mb-2">Digest Dashboard</h1>
          <p className="text-sm text-[#64748B] mb-5">
            Enter your <code className="text-[#DD2A7B]">DIGEST_ADMIN_SECRET</code> to view recent runs, fan out the weekly digest, or send a test.
          </p>
          <input
            data-testid="digest-admin-secret"
            type="password"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") tryUnlock(); }}
            placeholder="DIGEST_ADMIN_SECRET"
            className="nexus-input"
            autoFocus
          />
          <button
            data-testid="digest-admin-unlock"
            disabled={loading || !secret.trim()}
            onClick={tryUnlock}
            className="w-full nexus-btn-primary mt-4"
          >
            {loading ? "Checking…" : "Unlock"}
          </button>
          <p className="text-[10px] text-[#94A3B8] mt-4 leading-relaxed">
            Stored in <code>sessionStorage</code> — cleared when you close the tab.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="digest-admin-dashboard">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#DD2A7B] mb-1 flex items-center gap-1.5">
            <ShieldCheck size={11} /> Admin · operational
          </div>
          <h1 className="font-display text-4xl font-bold tracking-tight">Digest Dashboard</h1>
          <p className="text-[#64748B] text-sm mt-1">Last {runs.length} weekly run{runs.length === 1 ? "" : "s"} — health, fanout, and test sends.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="digest-refresh" onClick={refresh} disabled={loading} className="nexus-btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button data-testid="digest-dry-run" onClick={dryRun} disabled={busy} className="nexus-btn-secondary !py-1.5 !px-3 text-xs flex items-center gap-1.5">
            <AlertTriangle size={12} /> Dry run
          </button>
          <button data-testid="digest-real-run" onClick={realRun} disabled={busy} className="nexus-btn-primary !py-1.5 !px-3 text-xs flex items-center gap-1.5">
            <Send size={12} /> Run now
          </button>
        </div>
      </div>

      {totals && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Tile icon={Clock}      label="Runs recorded" value={totals.runs}   color="#DD2A7B" testid="tile-runs" />
          <Tile icon={CheckCircle2} label="Total sent"  value={totals.sent}   color="#10B981" testid="tile-sent" />
          <Tile icon={Users}      label="Avg active"   value={totals.avg_active} color="#0EA5E9" testid="tile-avg-active" />
          <Tile icon={AlertCircle} label="Total errors" value={totals.errors} color={totals.errors > 0 ? "#EF4444" : "#94A3B8"} testid="tile-errors" />
        </div>
      )}

      <div className="glass rounded-2xl p-4" data-testid="test-send-card">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#0EA5E9] font-semibold mb-2 flex items-center gap-1.5">
          <Mail size={11} /> Test send
        </div>
        <div className="flex flex-wrap gap-2 items-end">
          <input
            data-testid="test-email-input"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="creator@example.com — must be an existing NEXUS user"
            className="nexus-input flex-1 !min-w-[260px]"
          />
          <button
            data-testid="test-send-btn"
            onClick={testSend}
            disabled={busy || !testEmail.trim()}
            className="nexus-btn-primary flex items-center gap-2"
          >
            <Send size={13} /> Send test
          </button>
        </div>
      </div>

      <div className="glass-heavy rounded-3xl overflow-hidden" data-testid="runs-list">
        <div className="px-5 py-3 border-b border-slate-200 text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] font-semibold grid grid-cols-[1fr_90px_90px_90px_90px_50px] gap-2">
          <div>Ran at</div>
          <div className="text-right">Active</div>
          <div className="text-right">Sent</div>
          <div className="text-right">Skipped</div>
          <div className="text-right">Errors</div>
          <div></div>
        </div>
        {runs.length === 0 ? (
          <div className="p-10 text-center text-[#94A3B8] text-sm">
            <TrendingUp size={20} className="mx-auto mb-3 text-[#94A3B8]" />
            No runs yet. Hit <strong>Run now</strong> or wait for Monday 09:00 UTC.
          </div>
        ) : runs.map((r) => {
          const skipped = (r.results?.skipped_no_activity || 0) + (r.results?.skipped_no_email || 0);
          const errors = r.results?.errors || 0;
          const isOpen = expandedRun === r.ran_at;
          return (
            <div key={r.ran_at} data-testid={`run-${r.ran_at}`} className="border-b border-slate-100 last:border-0">
              <div
                onClick={() => setExpandedRun(isOpen ? null : r.ran_at)}
                className="px-5 py-3 grid grid-cols-[1fr_90px_90px_90px_90px_50px] gap-2 items-center hover:bg-slate-50 cursor-pointer"
              >
                <div>
                  <div className="text-sm text-[#0F172A] font-medium flex items-center gap-2">
                    {new Date(r.ran_at).toLocaleString()}
                    <HealthBadge health={r.health} />
                  </div>
                  <div className="text-[10px] text-[#94A3B8]">{r.source === "internal_scheduler" ? "internal cron" : "http endpoint"}</div>
                </div>
                <div className="text-right text-sm font-semibold text-[#0F172A]">{r.active_creators}</div>
                <div className="text-right text-sm font-semibold text-[#10B981]">{r.results?.sent || 0}</div>
                <div className="text-right text-sm text-[#64748B]">{skipped}</div>
                <div className={`text-right text-sm font-semibold ${errors > 0 ? "text-[#EF4444]" : "text-[#94A3B8]"}`}>{errors}</div>
                <div className="text-right text-xs text-[#94A3B8]">{isOpen ? "−" : "+"}</div>
              </div>
              {isOpen && (
                <div className="px-5 pb-4 bg-slate-50/50">
                  {r.health && !r.health.healthy && (
                    <div className="mb-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-xs p-2.5">
                      <strong>Unhealthy run</strong> · {r.health.reason}
                    </div>
                  )}
                  <div className="max-h-64 overflow-y-auto text-xs space-y-1">
                    {(r.per_creator || []).slice(0, 60).map((c) => (
                      <div key={`${c.user_id}-${c.status}`} className="flex justify-between border-b border-slate-100 py-1">
                        <span className="text-[#64748B] font-mono">{c.user_id}</span>
                        <span className={
                          c.status === "sent" ? "text-[#10B981] font-semibold" :
                          c.status?.startsWith("skipped") ? "text-[#94A3B8]" :
                          "text-[#EF4444]"
                        }>{c.status}</span>
                      </div>
                    ))}
                    {(r.per_creator?.length || 0) > 60 && (
                      <div className="text-[#94A3B8] pt-2">+ {(r.per_creator.length - 60)} more (response capped at 200)</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Tile({ icon: Icon, label, value, color, testid }) {
  return (
    <div data-testid={testid} className="glass rounded-2xl p-4 flex items-start gap-3">
      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}1A` }}>
        <Icon size={16} style={{ color }} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">{label}</div>
        <div className="font-display font-bold text-2xl mt-0.5" style={{ color: "#0F172A" }}>{value}</div>
      </div>
    </div>
  );
}

function HealthBadge({ health }) {
  if (!health) return null;
  const map = {
    ok:       { bg: "rgba(16,185,129,0.12)",  fg: "#059669", label: "healthy" },
    warn:     { bg: "rgba(245,158,11,0.14)",  fg: "#B45309", label: "warn" },
    critical: { bg: "rgba(239,68,68,0.12)",   fg: "#DC2626", label: "critical" },
  };
  const c = map[health.severity] || map.ok;
  return (
    <span
      data-testid="health-badge"
      title={health.reason}
      className="px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider"
      style={{ background: c.bg, color: c.fg }}
    >
      {c.label}
    </span>
  );
}
